import React, { useMemo, useState } from "react";
import { Table } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { KpiIcon } from "../../../../DataManagment/Quantative/component/KpiIcons";
import NodataFound from "../../../../../img/no.png";
import RowActionMenu from "./RowActionMenu";
import {
  SUPPLIER_STATUS,
  cardSurface,
  filterBarStyle,
  tableCardSurface,
  thStyle,
} from "./supplierManagementStyles";
import { supplierSearchText } from "./supplierManagementHelpers";

/**
 * Registered-suppliers tab: filter bar (search + status), table body,
 * empty state. Data + refresh callables come from the parent hook.
 */
const RegisteredList = ({
  rows,
  loading,
  openMenuId,
  setOpenMenuId,
  onOpenDetails,
}) => {
  const history = useHistory();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const filtered = useMemo(() => {
    if (!rows?.length) return [];
    const q = search.trim().toLowerCase();
    const tokens = q.split(/\s+/).filter(Boolean);

    return rows.filter((row) => {
      const haystack = supplierSearchText(row);
      if (tokens.length && !tokens.every((t) => haystack.includes(t))) {
        return false;
      }
      if (statusFilter === "active" && row.status !== SUPPLIER_STATUS.ACTIVE) return false;
      if (statusFilter === "inactive" && row.status === SUPPLIER_STATUS.ACTIVE) return false;
      return true;
    });
  }, [rows, search, statusFilter]);

  const totalRegistered = rows?.length ?? 0;
  const filteredCount = filtered.length;
  const hasActiveFilters = Boolean(search.trim()) || statusFilter !== "all";

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 12,
          padding: 48,
          ...cardSurface,
        }}
      >
        <div className="kpi-spinner" />
        <span style={{ fontSize: 13, color: "var(--ink-4)", fontWeight: 600 }}>
          Loading suppliers…
        </span>
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <div
        style={{
          ...cardSurface,
          textAlign: "center",
          padding: "48px 24px",
        }}
      >
        <img
          style={{ opacity: 0.2, maxWidth: 280, width: "100%" }}
          src={NodataFound}
          alt=""
        />
        <p style={{ marginTop: 16, color: "var(--ink-3)", fontSize: 14, fontWeight: 600 }}>
          No registered suppliers yet
        </p>
      </div>
    );
  }

  return (
    <>
      <div style={filterBarStyle}>
        <div
          style={{
            position: "relative",
            flex: "1 1 220px",
            minWidth: 180,
            maxWidth: 420,
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 11,
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--ink-4)",
              pointerEvents: "none",
            }}
          >
            <KpiIcon name="search" size={14} />
          </div>
          <input
            className="kpi-input"
            style={{ paddingLeft: 34, paddingRight: search ? 32 : 12 }}
            placeholder="Search company, industry, contact, address…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search registered suppliers"
          />
          {search ? (
            <button
              type="button"
              onClick={() => setSearch("")}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                fontSize: 18,
                color: "var(--ink-4)",
                lineHeight: 1,
                padding: 0,
              }}
              aria-label="Clear search"
            >
              ×
            </button>
          ) : null}
        </div>

        <div
          style={{ width: 1, height: 26, background: "var(--rule)", flexShrink: 0 }}
          aria-hidden
        />

        <select
          className={`kpi-inline-select${statusFilter !== "all" ? " active" : ""}`}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          title="Filter by status"
          aria-label="Status filter"
        >
          <option value="all">All statuses</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>

        {hasActiveFilters ? (
          <button type="button" className="kpi-btn-secondary" onClick={clearFilters}>
            Clear filters
          </button>
        ) : null}

        <span
          style={{
            marginLeft: "auto",
            fontSize: 12,
            color: "var(--ink-4)",
            fontFamily: "var(--font-mono)",
            whiteSpace: "nowrap",
          }}
        >
          {hasActiveFilters && filteredCount !== totalRegistered
            ? `Showing ${filteredCount} of ${totalRegistered}`
            : `${totalRegistered} registered`}
        </span>
      </div>

      {filteredCount === 0 ? (
        <div
          style={{
            ...cardSurface,
            textAlign: "center",
            padding: "40px 24px",
          }}
        >
          <p style={{ color: "var(--ink-3)", fontSize: 14, fontWeight: 600, marginBottom: 16 }}>
            No suppliers match your filters
          </p>
          <button type="button" className="kpi-btn-secondary" onClick={clearFilters}>
            Clear filters
          </button>
        </div>
      ) : (
        <div
          className="supplier-mgmt-table-wrap"
          style={tableCardSurface}
        >
          <div className="supplier-mgmt-table-scroll">
            <Table hover className="mb-0 supplier-mgmt-table">
              <thead>
                <tr>
                  <th className="supplier-mgmt-sr-cell" style={thStyle}>Sr</th>
                  <th style={{ ...thStyle, width: 200 }}> Company</th>
                  <th style={thStyle}>Industry</th>
                  <th style={thStyle}>Contact</th>
                  <th style={thStyle}>Address</th>
                  <th style={{ ...thStyle, width: 120 }}>Employees</th>
                  <th style={{ ...thStyle, width: 88 }}>Status</th>
                  <th style={{ ...thStyle, width: 56, textAlign: "center" }} />
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, index) => {
                  const contact = row.contactName || "—";
                  return (
                    <tr key={row.mappingId ?? row.supplierId ?? index}>
                      <td className="supplier-mgmt-sr-cell">{index + 1}</td>
                      <td>
                        <div style={{ fontWeight: 600, color: "var(--ink-2)" }}>
                          {row.companyName || "—"}
                        </div>
                        {row.entityType ? (
                          <span className="d-block">
                            {row.entityType.replace(/_/g, " ")}
                          </span>
                        ) : null}
                      </td>
                      <td>
                        <div>{row.industryName || "—"}</div>
                        {row.subIndustryName ? (
                          <span className="d-block">{row.subIndustryName}</span>
                        ) : null}
                      </td>
                      <td>
                        <span style={{ fontWeight: 500 }}>{contact}</span>
                        {row.email ? <span className="d-block">{row.email}</span> : null}
                        {row.mobile ? <span className="d-block">{row.mobile}</span> : null}
                      </td>
                      <td style={{ maxWidth: 240 }}>
                        <span className="small">{row.address || "—"}</span>
                      </td>
                      <td className="small">{row.employeeSizeCategory || "—"}</td>
                      <td>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "3px 10px",
                            borderRadius: 999,
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            fontFamily: "var(--font-mono)",
                            background:
                              row.status === SUPPLIER_STATUS.ACTIVE ? "#dcfce7" : "#fee2e2",
                            color:
                              row.status === SUPPLIER_STATUS.ACTIVE ? "#15803d" : "#b91c1c",
                            border: `1px solid ${row.status === SUPPLIER_STATUS.ACTIVE ? "#86efac" : "#fecaca"}`,
                          }}
                        >
                          {row.status === SUPPLIER_STATUS.ACTIVE ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <RowActionMenu
                          menuId={`registered-${row.supplierId ?? row.mappingId ?? index}`}
                          openMenuId={openMenuId}
                          setOpenMenuId={setOpenMenuId}
                          items={[
                            {
                              key: "view",
                              label: "View details",
                              icon: "search",
                              onClick: () => onOpenDetails(row.supplierId),
                            },
                            {
                              key: "active-assessments",
                              label: "Active assessments",
                              icon: "audit",
                              onClick: () =>
                                row.supplierId &&
                                history.push(`/assessment/active/${row.supplierId}`),
                            },
                            {
                              key: "past-assessments",
                              label: "Past assessments",
                              icon: "audit",
                              onClick: () =>
                                row.supplierId &&
                                history.push(`/assessment/past/${row.supplierId}`),
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </>
  );
};

export default RegisteredList;
